import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const LOGO_URL = "/pt-logo.png?v=2";
const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PowerWashingContact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact/power-washing`, formData);
      setIsSuccess(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to send your request. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="power-washing-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-2xl text-slate-900 font-['Outfit'] tracking-tight hidden sm:block">Platinum Touch</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Outfit'] tracking-tight mb-4">
            Power Washing Services
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Restore your property's appearance with professional power washing. Contact us for a free estimate and we'll get back to you as soon as possible.
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden animate-slide-up">
          <div className="grid grid-cols-1 md:grid-cols-5 h-full">
            
            {/* Contact Info Sidebar */}
            <div className="md:col-span-2 bg-slate-900 text-white p-8 md:p-12 flex flex-col justify-center relative overflow-hidden">
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-emerald-500/20 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 rounded-full bg-blue-500/20 blur-3xl"></div>
              
              <div className="relative z-10 space-y-10">
                <div>
                  <h3 className="text-2xl font-bold font-['Outfit'] mb-2">Get in Touch</h3>
                  <p className="text-slate-400 text-sm">We're here to help with all your exterior cleaning needs.</p>
                </div>
                
                <div className="space-y-6">
                  <a href="tel:732-732-4310" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                      <Phone className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 font-medium mb-1">Phone</p>
                      <p className="text-lg font-semibold tracking-wide group-hover:text-emerald-400 transition-colors">(732) 732-4310</p>
                    </div>
                  </a>
                  
                  <a href="mailto:bookings@platinumtouchnj.com" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 font-medium mb-1">Email</p>
                      <p className="text-base font-medium break-all group-hover:text-blue-400 transition-colors">bookings@platinumtouchnj.com</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-3 p-8 md:p-12">
              {isSuccess ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-12 animate-fade-in">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 font-['Outfit']">Thank you!</h3>
                  <p className="text-slate-600 max-w-sm">
                    We've received your request and will contact you shortly with your free estimate.
                  </p>
                  <Button 
                    onClick={() => setIsSuccess(false)}
                    variant="outline"
                    className="mt-6 border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Send Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Full Name *</label>
                      <Input 
                        placeholder="John Doe" 
                        required
                        value={formData.name}
                        onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                        className="bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Phone Number *</label>
                      <Input 
                        placeholder="(555) 123-4567" 
                        required
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                        className="bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 h-11"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Email Address *</label>
                    <Input 
                      placeholder="john@example.com" 
                      required
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                      className="bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      Property Address <span className="text-slate-400 font-normal">(Optional)</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="123 Main St, Lakewood, NJ" 
                        value={formData.address}
                        onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                        className="bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 h-11 pl-9"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">How can we help? *</label>
                    <Textarea 
                      placeholder="Tell us what needs power washing (e.g. driveway, deck, siding...)" 
                      required
                      rows={4}
                      value={formData.message}
                      onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                      className="bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 rounded-xl text-base font-semibold transition-all shadow-md hover:shadow-lg"
                  >
                    {isSubmitting ? 'Sending Request...' : (
                      <>
                        Request Free Quote <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 text-center space-y-4">
          <p className="text-xl sm:text-2xl font-bold text-slate-800 tracking-wide">
            Phone: <a href="tel:732-732-4310" className="text-emerald-600 hover:text-emerald-700 transition-colors">(732) 732-4310</a>
          </p>
          <p className="text-xl sm:text-2xl font-bold text-slate-800 tracking-wide">
            Email: <a href="mailto:bookings@platinumtouchnj.com" className="text-blue-600 hover:text-blue-700 transition-colors">bookings@platinumtouchnj.com</a>
          </p>
        </div>
      </main>

      <footer className="py-8 text-center border-t border-slate-200 mt-auto">
        <p className="text-slate-500 text-sm">
          &copy; {new Date().getFullYear()} Platinum Touch. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
